import ProtectedPageSidebar from 'engage/components/Sidebar/ProtectedPageSidebar';
import { useUIContext } from 'engage/context/UIContext';
import { withPermissionCheck } from 'engage/hocs/withPermissionCheck';
import React, { useEffect, useMemo } from 'react';
import configs from '../../../../conf';
import VormatsAPI from 'common/apis/VormatsAPI';
import { timeStampToMs } from 'engage/utils/TimeUtil';
import ResourceHelper from 'engage/utils/ResourceHelper';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const config = configs[process.env.APP_CONF];

const MESSAGE_TYPE = {
    REQUEST_VIDEO_ID: 'request-video-id',
    REQUEST_TOKEN: 'request-token',
    REQUEST_STORY_DETAILS: 'request-story-details',
    GO_TO_HOME: 'go-to-home',
    OPEN_SIDEBAR: 'open-sidebar',
    LANGUAGE_CHANGED: 'language-changed',
    REQUEST_LANGUAGE: 'request-language',
    GO_TO_VIDEO_DETAILS: 'go-to-video-details',
}

function EdgeBlockers() {
    const style = {
        position: 'fixed',
        top: 0,
        bottom: 0,
        zIndex: 9999,
        touchAction: 'none',
        pointerEvents: 'auto',
    };

    return (
        <>
            <div style={{ ...style, width: '3px', left: 0 }} />
            <div style={{ ...style, width: '10px', right: 0 }} />
        </>
    );
}

const IFRAME_NAME = 'advancedEditor';

const AdvancedEditor = ({ videoId, initialVideoDetails }) => {

    const { toggleMiniSidebar } = useUIContext();
    const [showSidebar, setShowSidebar] = React.useState(false);

    const iframeRef = React.useRef(null);

    const { i18n } = useTranslation();

    const history = useHistory();

    const sidebarStyles = useMemo(() => {
        const base = {
            position: 'absolute',
            left: '40px',
            borderRadius: '8px',
            backgroundColor: '#fff',
            overflowY: 'auto',
            filter: 'drop-shadow(0px 4px 24px rgba(0, 0, 0, 0.4))',
            transition: 'top 250ms ease-in-out, opacity 150ms ease-in-out',
            overflow: 'visible',
        };
        if (showSidebar) {
            return {
                ...base,
                top: '40px',
                opacity: 1,
                visibility: 'visible',
            };
        } else {
            return {
                ...base,
                top: '140px',
                opacity: 0,
                visibility: 'hidden',
            };
        }
    }, [showSidebar]);


    useEffect(() => {
        toggleMiniSidebar(false);

        const messagesHandler = async (event) => {
            if (event?.data) {
                let payload = '';
                switch (event.data.type) {
                    case MESSAGE_TYPE.REQUEST_VIDEO_ID:
                        payload = videoId;
                        break;
                    case MESSAGE_TYPE.REQUEST_TOKEN:
                        payload = await VormatsAPI.getAccessJwtToken();
                        break;
                    case MESSAGE_TYPE.REQUEST_STORY_DETAILS:
                        const preparedScenes = initialVideoDetails.scenes.map(scene => {
                            const thumbnail = ResourceHelper.getResourceByLabel(scene, 'defaultStaticThumbnail');
                            const normalizedResources = ResourceHelper.getAllResourcesByType(scene, 'normalized');
                            const normalizedResourceId = normalizedResources.find(resource => resource.label === 'enhanced' || resource.label === 'normalized')?.id;

                            return {
                                id: scene.id,
                                width: scene.dimensions?.width ?? null,
                                height: scene.dimensions?.height ?? null,
                                duration: scene.duration,
                                trimStart: timeStampToMs(scene.trimStart),
                                trimEnd: timeStampToMs(scene.trimEnd),
                                videoPath: scene.video,
                                normalizedResourceId,
                                thumbnail: thumbnail?.url,
                                thumbnailResourceId: thumbnail?.id,
                                music: scene.music,
                                videoVolume: scene.videoVolume,
                                name: scene.name,
                                narrative: scene.narrative,
                                orientation: scene.orientation,
                                script: scene.script,
                                sort: scene.sort,
                                textPanel: scene.nameCard,
                            }
                        });
                        payload = JSON.stringify({ ...initialVideoDetails, scenes: preparedScenes });
                        break;
                    case MESSAGE_TYPE.GO_TO_HOME:
                        history.push('/eg/home');
                        break;
                    case MESSAGE_TYPE.OPEN_SIDEBAR:
                        setShowSidebar(true);
                        break;
                    case MESSAGE_TYPE.REQUEST_LANGUAGE:
                        payload = i18n?.language;
                        break;
                    case MESSAGE_TYPE.GO_TO_VIDEO_DETAILS:
                        history.push(`/eg/video/${videoId}`);
                        break;
                    default:
                        break;
                }
                const frame = window.frames[IFRAME_NAME];
                frame?.postMessage({ payload, requestId: event.data.requestId }, config.advancedVideoEditorUrl);
            }
        }

        addEventListener("message", messagesHandler);

        return () => {
            removeEventListener("message", messagesHandler);
            setShowSidebar(false);
        }
    }, [])

    useEffect(() => {
        const frame = window.frames[IFRAME_NAME];
        if (frame && i18n?.language) {
            frame.postMessage({ payload: i18n?.language, type: MESSAGE_TYPE.LANGUAGE_CHANGED }, config.advancedVideoEditorUrl);
        }
    }, [i18n?.language]);

    useEffect(() => {

        const handleBlur = () => {
            setTimeout(() => {
                if (document.activeElement.tagName === "IFRAME") {
                    setShowSidebar(false);
                }
            });
        }

        if (showSidebar) {
            window.focus();
            window.addEventListener("blur", handleBlur);
        } else {
            window.removeEventListener("blur", handleBlur);
        }

        return () => {
            window.removeEventListener("blur", handleBlur);
        }
    }, [showSidebar])

    return (
        <>
            <EdgeBlockers />
            <div style={
                {
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'row',
                    paddingRight: '0',
                    height: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <iframe name={IFRAME_NAME} ref={iframeRef} src={config.advancedVideoEditorUrl} style={{ border: 'none', width: '100%', height: '100vh' }} />
                <div style={sidebarStyles}>
                    <ProtectedPageSidebar inModal={true} reset={!showSidebar} />
                </div>
            </div >
        </>
    )
}

export default withPermissionCheck(AdvancedEditor, false);